<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import type { ContractCategory } from '@/data/seedMember'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import ContractTerminateAction from '@/components/ui/ContractTerminateAction.vue'
import ContractCertificateAction from '@/components/ui/ContractCertificateAction.vue'
import { canDownloadCertificate } from '@/config/contractCertificate'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()
const { contracts, reload } = useContracts()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.contracts.title')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

/* --- Onglets de filtre (Tous / Santé / Assurance) avec compteurs. --- */
const TABS = ['all', 'sante', 'assurance'] as const
type Tab = (typeof TABS)[number]
const activeTab = ref<Tab>('all')
const counts = computed<Record<Tab, number>>(() => ({
  all: contracts.value.length,
  sante: contracts.value.filter((c) => c.category === 'sante').length,
  assurance: contracts.value.filter((c) => c.category === 'assurance').length,
}))
const filtered = computed(() =>
  activeTab.value === 'all'
    ? contracts.value
    : contracts.value.filter((c) => c.category === (activeTab.value as ContractCategory)),
)

/* --- Contrat sélectionné : défaut = premier contrat. Une référence passée
 *     dans l'URL (?ref=...) le présélectionne — ex. depuis « Souscrire un
 *     produit » où un contrat déjà souscrit renvoie ici via « Voir les détails ». */
const selectedRef = ref<string | null>(
  typeof route.query.ref === 'string' ? route.query.ref : null,
)
const selected = computed(() => {
  const list = contracts.value
  if (!list.length) return null
  return list.find((c) => c.reference === selectedRef.value) ?? list[0]
})
function select(reference: string) {
  selectedRef.value = reference
}

/* --- Garanties : repliées au-delà de 5 lignes, dépliables. --- */
const COLLAPSED = 5
const expanded = ref(false)
const shownGuarantees = computed(() => {
  const g = selected.value?.guarantees ?? []
  return expanded.value ? g : g.slice(0, COLLAPSED)
})
const hasMore = computed(() => (selected.value?.guarantees.length ?? 0) > COLLAPSED)
watch(selected, () => (expanded.value = false))

/** Icône selon le type de contrat. */
function iconFor(c: { name: string; category: ContractCategory }): 'health' | 'globe' | 'house' | 'shield' {
  const n = c.name.toLowerCase()
  if (n.includes('habitation')) return 'house'
  if (n.includes('europe') || n.includes('étranger') || n.includes('etranger')) return 'globe'
  if (c.category === 'sante') return 'health'
  return 'shield'
}

/** Date ISO → format localisé court, ou « — » si absente. */
function fmtDate(iso: string | null): string {
  if (!iso) return t('auth.account.contracts.none')
  return new Date(iso).toLocaleDateString(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/* Actions sur le contrat sélectionné (résiliation + attestation) : composants dédiés. */
</script>

<template>
  <AccountLayout v-if="member" active="contracts">
    <section class="ct">
      <!-- Fil d'ariane + titre -->
      <nav class="ct__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.contracts.breadcrumbHome') }}</span>
        <span class="ct__crumb-sep" aria-hidden="true">›</span>
        <span class="ct__crumb-current">{{ t('auth.account.contracts.title') }}</span>
      </nav>
      <header class="ct__head">
        <h1 class="ct__title">{{ t('auth.account.contracts.title') }}</h1>
        <p class="ct__subtitle">{{ t('auth.account.contracts.subtitle') }}</p>
      </header>

      <div class="ct__grid">
        <!-- Colonne gauche : sélection + liste -->
        <div class="ct__list-col">
          <!-- Contrat sélectionné (mis en avant) -->
          <article v-if="selected" class="sel">
            <span class="sel__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M12 21s-7-4.4-9.3-8.6C1.2 9.6 2.6 6 6 6c2 0 3.2 1.2 4 2.4C10.8 7.2 12 6 14 6c3.4 0 4.8 3.6 3.3 6.4C19 16.6 12 21 12 21Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
              </svg>
            </span>
            <div class="sel__body">
              <span class="badge" :class="`badge--${selected.status}`">{{ t(`auth.account.status.${selected.status}`) }}</span>
              <h2 class="sel__name">{{ selected.name }}</h2>
              <p class="sel__num">{{ t('auth.account.contracts.number') }} : {{ selected.reference }}</p>
            </div>
            <!-- The wrapper is guarded as well as the button: `.sel__dl` owns a
                 grid cell in the banner, and leaving it empty on a terminated
                 contract would keep a visible gap where the CTA used to be. -->
            <div v-if="canDownloadCertificate(selected)" class="sel__dl">
              <ContractCertificateAction :contract="selected" variant="solid" />
            </div>
          </article>

          <!-- Onglets -->
          <div class="tabs" role="tablist">
            <button
              v-for="tab in TABS"
              :key="tab"
              type="button"
              role="tab"
              class="tabs__btn"
              :class="{ 'is-active': activeTab === tab }"
              :aria-selected="activeTab === tab"
              @click="activeTab = tab"
            >
              {{ t('auth.account.contracts.tabs.' + tab) }} ({{ counts[tab] }})
            </button>
          </div>

          <!-- Liste des contrats -->
          <ul class="clist">
            <li v-for="c in filtered" :key="c.reference">
              <button
                type="button"
                class="crow"
                :class="{ 'is-selected': selected && c.reference === selected.reference }"
                :aria-pressed="!!selected && c.reference === selected.reference"
                @click="select(c.reference)"
              >
                <span class="crow__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <template v-if="iconFor(c) === 'health'">
                      <path d="M12 21s-7-4.4-9.3-8.6C1.2 9.6 2.6 6 6 6c2 0 3.2 1.2 4 2.4C10.8 7.2 12 6 14 6c3.4 0 4.8 3.6 3.3 6.4C19 16.6 12 21 12 21Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                    </template>
                    <template v-else-if="iconFor(c) === 'globe'">
                      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                      <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" fill="none" stroke="currentColor" stroke-width="1.4"/>
                    </template>
                    <template v-else-if="iconFor(c) === 'house'">
                      <path d="M4 11l8-6 8 6v9H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                      <path d="M10 20v-5h4v5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                    </template>
                    <template v-else>
                      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                    </template>
                  </svg>
                </span>
                <span class="crow__info">
                  <span class="crow__name">{{ c.name }}</span>
                  <span class="crow__num">{{ t('auth.account.contracts.number') }} : {{ c.reference }}</span>
                </span>
                <span class="badge" :class="`badge--${c.status}`">{{ t(`auth.account.status.${c.status}`) }}</span>
              </button>
            </li>
          </ul>

          <!-- Contrat introuvable -->
          <article class="nf">
            <span class="nf__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M16 16l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </span>
            <div class="nf__body">
              <h3 class="nf__title">{{ t('auth.account.contracts.notFoundTitle') }}</h3>
              <p class="nf__desc">{{ t('auth.account.contracts.notFoundDesc') }}</p>
              <RouterLink class="nf__cta" :to="contactHref">{{ t('auth.account.contracts.notFoundCta') }}</RouterLink>
            </div>
          </article>
        </div>

        <!-- Colonne droite : détail du contrat -->
        <div v-if="selected" class="ct__detail">
          <!-- Informations générales -->
          <section class="panel">
            <h2 class="panel__title">{{ t('auth.account.contracts.generalTitle') }}</h2>
            <dl class="ginfo">
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.memberNumber') }}</dt><dd>{{ member.memberNumber }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.startDate') }}</dt><dd>{{ fmtDate(selected.general.startDate) }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.paymentMode') }}</dt><dd>{{ selected.general.paymentMode }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.endDate') }}</dt><dd>{{ fmtDate(selected.general.endDate) }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.paymentRhythm') }}</dt><dd>{{ selected.general.paymentRhythm }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.duration') }}</dt><dd>{{ t('auth.account.contracts.durationMonths', { count: selected.general.durationMonths }) }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.paymentAmount') }}</dt><dd>{{ selected.general.paymentAmount }}</dd></div>
              <div class="ginfo__row"><dt>{{ t('auth.account.contracts.fields.tacitRenewal') }}</dt><dd>{{ selected.general.tacitRenewal ? t('auth.account.contracts.yes') : t('auth.account.contracts.no') }}</dd></div>
            </dl>
          </section>

          <!-- Garanties -->
          <section class="panel">
            <h2 class="panel__title">{{ t('auth.account.contracts.guaranteesTitle') }}</h2>
            <dl class="guar">
              <div v-for="g in shownGuarantees" :key="g.label" class="guar__row">
                <dt>{{ g.label }}</dt>
                <dd>{{ g.value }}</dd>
              </div>
            </dl>
            <button v-if="hasMore" type="button" class="guar__more" @click="expanded = !expanded">
              {{ expanded ? t('auth.account.contracts.seeLess') : t('auth.account.contracts.seeMore') }}
            </button>
            <!-- Calaméo guarantee sheet: an external document, opened in a new
                 tab with the opener reference severed (rel) so the authenticated
                 connected space stays intact behind it. -->
            <a
              v-if="selected.guaranteeSheetUrl"
              class="guar__sheet"
              :href="selected.guaranteeSheetUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ t('auth.account.contracts.guaranteeSheet') }}
              <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                <path d="M8 4H5v11h11v-3M12 3h5v5M17 3l-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </section>

          <div class="ct__actions">
            <ContractTerminateAction :contract="selected" variant="solid" @terminated="reload" />
          </div>
        </div>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.ct { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.ct__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.ct__crumb-sep { color: var(--text-muted); }
.ct__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.ct__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.ct__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille 2 colonnes : liste + détail */
.ct__grid {
  display: grid;
  grid-template-columns: 1fr 1.25fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.ct__list-col { display: flex; flex-direction: column; gap: 1rem; }

/* Contrat sélectionné (bandeau mis en avant) */
.sel {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.9rem;
  row-gap: 0.6rem;
  padding: 1rem;
  border: 1px solid color-mix(in oklab, var(--primary) 45%, white);
  border-radius: var(--radius-xl);
  background: color-mix(in oklab, var(--color-peach) 35%, white);
  box-shadow: var(--shadow-sm);
}
.sel__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: var(--color-white);
}
.sel__body { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.sel__name { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.sel__num { font-size: var(--text-xs); color: var(--text-muted); }
.sel__dl { grid-column: 1 / -1; justify-self: start; }
@media (min-width: 30rem) {
  .sel__dl { grid-column: 3; grid-row: 1 / span 2; justify-self: end; }
}

/* Onglets */
.tabs { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.tabs__btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast), border-color var(--dur-fast), color var(--dur-fast);
}
.tabs__btn:hover { border-color: var(--primary); }
.tabs__btn.is-active { background: var(--color-chocolate); border-color: var(--color-chocolate); color: var(--color-white); }

/* Liste des contrats */
.clist { display: flex; flex-direction: column; gap: 0.6rem; }
.crow {
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--color-white);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--dur-fast), box-shadow var(--dur-fast), transform var(--dur-base) var(--ease-spring);
}
.crow:hover { border-color: var(--primary); box-shadow: var(--shadow-sm); transform: translateY(-1px); }
.crow.is-selected { border-color: var(--primary); box-shadow: 0 0 0 1px var(--primary) inset; }
.crow__icon {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-md);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
}
.crow__info { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.crow__name { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.crow__num { font-size: var(--text-xs); color: var(--text-muted); }

/* Contrat introuvable */
.nf {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1rem;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.nf__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  color: var(--text-muted);
  background: var(--color-white);
}
.nf__body { display: flex; flex-direction: column; gap: 0.2rem; }
.nf__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.nf__desc { font-size: var(--text-xs); color: var(--text-muted); }
.nf__cta {
  margin-top: 0.2rem;
  align-self: flex-start;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}
.nf__cta:hover { color: var(--color-cherry); }

/* Colonne détail */
.ct__detail { display: flex; flex-direction: column; gap: 1.25rem; }
.panel {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.panel__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }

/* Informations générales : grille 2 colonnes de paires clé/valeur */
.ginfo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem 1.25rem;
  margin: 0;
}
.ginfo__row { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.ginfo__row dt { font-size: var(--text-xs); color: var(--text-muted); }
.ginfo__row dt::after { content: " :"; }
.ginfo__row dd { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); margin: 0; }

/* Garanties : lignes alternées */
.guar { display: flex; flex-direction: column; margin: 0; }
.guar__row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 0.5rem 1rem;
  padding: 0.55rem 0.6rem;
  border-radius: var(--radius-sm);
}
.guar__row:nth-child(odd) { background: color-mix(in oklab, var(--color-peach) 18%, white); }
.guar__row dt { font-size: var(--text-sm); color: var(--color-chocolate); }
.guar__row dd { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); margin: 0; text-align: right; }
.guar__more {
  align-self: flex-start;
  margin-top: 0.2rem;
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
}
.guar__more:hover { color: var(--color-cherry); }

/* Guarantee sheet link (Calaméo PDF), below the list of guarantees. */
.guar__sheet {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.6rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.guar__sheet:hover { color: var(--color-cherry); }
.guar__sheet:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.ct__actions { display: flex; justify-content: center; }

/* Badges de statut */
.badge {
  align-self: flex-start;
  font-size: var(--text-xs);
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}
.badge--active { color: #1a7f45; background: #e6f6ec; }
.badge--pending { color: var(--color-chocolate); background: color-mix(in oklab, var(--color-peach) 35%, white); }
.badge--terminating,
.badge--retracting { color: var(--color-chocolate); background: color-mix(in oklab, var(--color-peach) 35%, white); }
.badge--terminated { color: var(--color-cherry); background: color-mix(in oklab, var(--color-cherry) 14%, white); }

@media (max-width: 64rem) {
  .ct__grid { grid-template-columns: 1fr; }
}
@media (max-width: 30rem) {
  .ginfo { grid-template-columns: 1fr; }
}
</style>
